import React, { Dispatch, forwardRef, SetStateAction, useMemo } from 'react';
import { useFocusRing } from '@react-aria/focus';
import {
  getInputSizeStyle,
  input_default_style,
  input_disabled_style,
  input_error_style,
  input_focus_style,
  input_wrapper_base_css,
} from '../../input/styles';
import classNames from 'classnames';
import { DatePickerIcon, DatePickerIconProps } from '../DatePickerIcon';
import { InputBase, InputBaseProps } from '../../input/InputBase';
import { IconArrowDirectionRight } from '../../icons/generated';
import { semantic_colors } from '../../../foundation';
import {
  date_picker_input_entered_disabled_style,
  getRangeFocusInputStyle,
  range_input_default_style,
  range_input_disabled_style,
  range_picker_focus_style,
} from '../styles';
import { useProviderConfig } from '../../provider';
import { AriaFocusProps } from '../../../types/common';

export type FocusType = null | 'start' | 'end';
export interface DateRangeInputProps
  extends Omit<InputBaseProps, 'leftAddon' | 'rightAddon' | 'alignment' | 'disabled'>,
    AriaFocusProps {
  readOnly?: boolean;
  placeholder?: string;
  focused?: FocusType;
  setFocused?: (focused: FocusType) => void;
  onClickRemoveButton?: () => void;
  inputValue: { start: string; end: string };
  fetchInputValue: Dispatch<SetStateAction<{ start: string; end: string }>>;
  modalOpen: boolean;
  disabled?: [boolean, boolean];
  isTimePickerFocused?: boolean;
}

export const DateRangeInput = forwardRef<HTMLInputElement, DateRangeInputProps & DatePickerIconProps>(
  (
    {
      status = 'info',
      readOnly = false,
      disabled,
      className,
      size = 'medium',
      placeholder,
      focused,
      setFocused,
      showRemoveIcon,
      unstableFocusVisible,
      onClickRemoveButton,
      inputValue,
      fetchInputValue,
      modalOpen,
      isTimePickerFocused,
    },
    ref,
  ) => {
    const { locale } = useProviderConfig();
    const { isFocusVisible, focusProps } = useFocusRing({ within: true, isTextInput: true });
    const status_style = {
      ...input_default_style,
      ...((unstableFocusVisible ?? isFocusVisible) && input_focus_style),
      ...(disabled?.[0] && disabled[1] && input_disabled_style),
      ...(status && status === 'error' && input_error_style),
    };

    const { startValue, startPlaceholder, endValue, endPlaceholder } = useMemo(
      () => ({
        startPlaceholder: focused === 'start' && placeholder ? placeholder : locale?.DatePickerRange.placeholder[0],
        endPlaceholder: focused === 'end' && placeholder ? placeholder : locale?.DatePickerRange.placeholder[1],
        startValue: focused === 'start' && placeholder ? '' : inputValue.start,
        endValue: focused === 'end' && placeholder ? '' : inputValue.end,
      }),
      [focused, inputValue, placeholder],
    );

    return (
      <div
        className={classNames('pds-date-range-input', className)}
        css={[
          input_wrapper_base_css,
          getInputSizeStyle(size),
          status_style,
          range_picker_focus_style,
          getRangeFocusInputStyle(size),
        ]}
        {...(isTimePickerFocused && focusProps)}
        ref={ref}
      >
        <InputBase
          ref={(input) => {
            if (focused === 'start' && !isTimePickerFocused) {
              input?.focus();
            } else if (focused !== 'start') {
              input?.blur();
            }
          }}
          className={classNames(status, 'start', { focused: focused === 'start' })}
          css={[
            {
              ...(disabled?.[0] && range_input_disabled_style),
              ...(disabled?.[0] && inputValue?.start && date_picker_input_entered_disabled_style),
            },
          ]}
          placeholder={startPlaceholder}
          value={startValue}
          style={range_input_default_style}
          readOnly={readOnly || !modalOpen}
          onFocus={() => modalOpen && setFocused?.('start')}
          onClick={() => setFocused?.('start')}
          disabled={disabled?.[0]}
          onChange={(e) => {
            fetchInputValue(({ end }) => ({
              start: e.target.value,
              end,
            }));
          }}
          endElement={
            <IconArrowDirectionRight
              size={12}
              color={
                disabled?.[0] || disabled?.[1] ? semantic_colors.content.disabled : semantic_colors.content.tertiary
              }
            />
          }
          size={size}
        />
        <InputBase
          ref={(input) => {
            if (focused === 'end' && !isTimePickerFocused) {
              input?.focus();
            } else if (focused !== 'end') {
              input?.blur();
            }
          }}
          className={classNames(status, 'end', { focused: focused === 'end' })}
          css={[
            {
              ...(disabled?.[1] && range_input_disabled_style),
              ...(disabled?.[1] && inputValue?.end && date_picker_input_entered_disabled_style),
            },
          ]}
          placeholder={endPlaceholder}
          value={endValue}
          style={range_input_default_style}
          readOnly={readOnly || !modalOpen}
          status={status}
          onFocus={() => modalOpen && setFocused?.('end')}
          onClick={() => setFocused?.('end')}
          disabled={disabled?.[1]}
          onChange={(e) => {
            fetchInputValue(({ start }) => ({
              end: e.target.value,
              start,
            }));
          }}
          endElement={<DatePickerIcon showRemoveIcon={showRemoveIcon} onClick={onClickRemoveButton} />}
          size={size}
        />
      </div>
    );
  },
);
